// Where a turn began, and how far back a take-back may reach (§29 U2).
//
// **A turn boundary is the side to move changing — or, with one army left,
// the round moving (§31 T2) — never a list of command types.** `endTurn` decides it that way and so does `planRewind`; a list of
// "commands that spend a turn" is an exclusion somebody has to remember to
// extend, whose failure is silent. Two things then come out right for free:
// Industrial Titan (several moves inside one turn, one boundary) and a run
// with several camps (your turn is bounded by *your* clock, not by anybody
// else's move).
//
// It lives in `src/engine/rules/` rather than beside the store because U3's server
// needs the same walk and `server/` imports `src/engine/rules` only. Pure: it reads
// no clock, rolls nothing, and every iteration order is fixed.
//
// **Undo is still not a command.** This module answers questions *about* a
// log; rewriting one stays in `GameStore`, and the reducer never sees an undo.

import type { Side } from "./board";
import {
  applyCommand,
  type Command,
  type GameState,
  IllegalCommandError
} from "./commands";
import {
  beltDirAt,
  beltOnAt,
  beltOwnerAt,
  blueprintDirAt,
  blueprintOwnerAt,
  heapAt,
  structureAt
} from "./industry/state";
import { structureHealthOf } from "./industry/structures";
import { healthOf, pieceAt, platingAt, variantAt } from "./position";

/** A board at the moment it became a side's turn, and where the log was. */
export interface TurnStart {
  /**
   * The last log index applied to reach this board.
   *
   * So the turn's own commands begin at `at + 1`, and **-1 is a real answer**:
   * it is the base state, which is where a continued run starts from since
   * `restore` lands a snapshot with an empty log.
   */
  at: number;
  state: GameState;
}

export interface TurnWalk {
  /** Every point at which it became `side`'s turn, in log order. */
  starts: TurnStart[];
  /** The board with the whole log replayed — what is on screen. */
  state: GameState;
}

/**
 * Replay `base` over `log`, recording every index at which it became `side`'s
 * turn.
 *
 * `from` is the last index of the prologue that is *not* walked — the floor a
 * caller has already found (`new-game`), replayed but never offered as a
 * boundary except by the board it produces. Safe to hold the states it
 * collects: `applyCommand` never mutates its argument, which the store already
 * depends on.
 *
 * One replay of the log, so it is a click's worth of work and not a frame's.
 * Callers ask it when a button is pressed or a row is drawn, never per render.
 */
export function walkTurns(
  base: GameState,
  log: readonly Command[],
  side: Side,
  from = -1
): TurnWalk {
  let state = base;
  for (let i = 0; i <= from; i++) state = applyCommand(state, log[i]);

  const starts: TurnStart[] = [];
  if (state.position.turn === side) starts.push({ at: from, state });
  let previous = state.position.turn;
  let round = state.position.round;
  for (let i = from + 1; i < log.length; i++) {
    state = applyCommand(state, log[i]);
    const turn = state.position.turn;
    // **Or the round moving** (§31 T1, T2): with one army on the board End
    // Turn hands the clock back to the same side, and the turn it ended must
    // still be sealed — the round is the one count that moves on every handoff.
    const ended = previous !== side || state.position.round !== round;
    if (turn === side && ended) starts.push({ at: i, state });
    previous = turn;
    round = state.position.round;
  }
  return { starts, state };
}

/** The last index in `log` holding a command of that type, or -1. */
export function lastIndexOf(log: readonly Command[], type: Command["type"]): number {
  for (let i = log.length - 1; i >= 0; i--) if (log[i].type === type) return i;
  return -1;
}

/**
 * Machine policy: free, unlimited, and its own inverse.
 *
 * **Settings are not actions, so Undo steps over them.** To undo a target you
 * set it back — nothing was spent and nothing moved — and a player who set
 * three policies and then laid a belt means the belt when they press Undo.
 * The reducer treats these exactly as it treats a build (owner-bound and
 * turn-bound since §29 U1); what is different is only what a take-back means.
 */
export const SETTING_COMMANDS: ReadonlySet<Command["type"]> = new Set([
  "set-target",
  "set-dump",
  "set-handoff",
  "set-king-task",
  "set-store-policy",
  "set-reserve",
  "toggle-belt"
]);

export const isSetting = (command: Command): boolean => SETTING_COMMANDS.has(command.type);

/**
 * What a player did on their own clock — the family Undo takes back.
 *
 * **Listed, because the negative definition was the bug.** §1 defined an
 * action as anything that is not a setting, which quietly made the *run's* own
 * commands takeable: the director issues `next-stage`, `reveal-region`,
 * `clear-front` and the rest on whoever's clock it is, with no owner, so
 * undoing the board's expansion and playing on again bought infinite moves.
 * Reported from a real run, 2026-09-09.
 *
 * `turns.test.ts` reflects over the reducer's case labels and fails if a
 * command is in none of the three families or in two of them.
 */
export const PLAYER_COMMANDS = [
  "move",
  "re-aim",
  // §31 S8: a turret turned with a move.
  "aim-turret",
  "pass",
  "drill",
  "rally",
  "lance",
  "sacrifice",
  "empower",
  "upgrade",
  "forge",
  "build",
  "place-structure",
  "demolish",
  // Trains (§31 S7): the player's own acts.
  "drive-train",
  "board-train",
  "alight-train",
  "lay-belt",
  "lay-belt-line",
  "lay-belt-path",
  "rotate-belt",
  "remove-belt",
  "mine",
  "load-belt",
  "pave",
  "play-card",
  // A trophy exchanged or kept (§31 A2): the player's own act, so takeable.
  "claim-trophy",
  "keep-trophy",
  // Equipment handed between Monarchs (§31 A0): the player's own act.
  "give-equipment",
  // An Oil-Stone Scepter seizure (§31 A4): the player's own act.
  "seize",
  // A fuse (§31 P4): the player's own act, like `upgrade`.
  "fuse"
] as const;

const PLAYER_COMMAND_SET: ReadonlySet<Command["type"]> = new Set(PLAYER_COMMANDS);

/**
 * Nobody's action: the run director's commands, the dev tools, and the two
 * commands that made the game.
 *
 * **The table is the type, not the runtime rule.** `undoFloor` never reads it
 * — it asks whether a command is a player's or a setting, so a type nobody
 * classified floors by construction rather than by being remembered here. What
 * this list buys is `FloorCommand`, which the run director's producers declare
 * so that a director step carrying a takeable command does not compile.
 */
export const FLOOR_COMMANDS = [
  "new-game",
  "burn-card",
  "next-stage",
  "reveal-region",
  "clear-front",
  "grant-card",
  "grant-token",
  "surrender",
  "set-despair",
  "set-power",
  "dev-defeat",
  "dev-grow",
  "dev-collapse",
  "dev-material"
] as const;

/**
 * A command the run director may emit.
 *
 * `src/engine/ai/`'s director producers — `RunStep`, `stageCommand`, `despairStep` —
 * return this rather than `Command`, so the hole that made the run's own
 * commands undoable cannot be reopened by a new step. A side's *own* plan
 * (`planTurn`'s commands) is that side acting on its clock and stays `Command`.
 */
export type FloorCommand = Extract<Command, { type: (typeof FLOOR_COMMANDS)[number] }>;

/**
 * The last index holding something that is neither a player's action nor a
 * setting — a floor no undo may cross.
 *
 * **The default is floor, deliberately.** Asked negatively against the one
 * list that is safe to be wrong about: forget to classify a command and the
 * worst that happens is a take-back stops early, where forgetting it in the
 * other direction hands the player a run command to undo.
 *
 * Exported for its test alone — `undoFloor` is the door everything else asks
 * through. An unclassified command cannot be tested through that door, because
 * `applyCommand` refuses a type it does not know and every replay would throw
 * before the floor was ever measured.
 */
export function lastFloor(log: readonly Command[]): number {
  for (let i = log.length - 1; i >= 0; i--) {
    const type = log[i].type;
    if (!PLAYER_COMMAND_SET.has(type) && !SETTING_COMMANDS.has(type)) return i;
  }
  return -1;
}

/**
 * The last log index no undo may cross, for this side.
 *
 * The greater of two floors, each for its own reason:
 *
 * - **The start of your current turn.** Undo takes back *your* turn, one
 *   action at a time; the turn before it is gone. In the solo opening the
 *   clock never leaves you (`nextSide` skips a side with no pieces), but End
 *   Turn still seals the turn (§31 T2): the round moves, and `walkTurns`
 *   reads that as a boundary.
 * - **The last command that was nobody's action** (`lastFloor`). It used to be
 *   two named cases and is now the rule they were both instances of.
 *   `new-game` is not a move — it is the command that made the board, the
 *   level, the deck and the seed — so crossing it returns you to a *different
 *   game*: two clicks of Undo on turn one of a run left the classic sixty-four
 *   squares, thirty-two pieces and no run at all. `burn-card` ends a rewind's
 *   log on a board earlier than the play, so undoing across it hands the card
 *   back and the rewind is free for ever (`rewind.ts` has the argument). And
 *   everything the run director does on your clock — the front turning over,
 *   ground opening, a camp landing — is neither: undoing the board's expansion
 *   and playing on bought infinite moves.
 */
export function undoFloor(base: GameState, log: readonly Command[], side: Side): number {
  return planUndo(base, log, side).floor;
}

/**
 * The index Undo would remove, or -1 when there is nothing above the floor.
 *
 * Settings sit above the floor without being takeable, so this is not
 * `log.length - 1`: a turn whose only commands are three targets has nothing
 * to take back, and the button has to say so rather than popping one.
 */
export function lastAction(log: readonly Command[], floor: number): number {
  for (let i = log.length - 1; i > floor; i--) if (!isSetting(log[i])) return i;
  return -1;
}

/**
 * Why Undo would do nothing, or null when it would.
 *
 * **One function, two readers, no way to disagree.** The store decides whether
 * to do it and the chrome decides what to grey; a button that is offered and
 * then does nothing is the silent no-op this interaction layer is written
 * against. `base` is private to the store, so the store exposes the question,
 * the way `rewindCard` does.
 *
 * A union rather than a boolean, and deliberately open: the chrome adds the
 * reasons it alone knows (a room it must not truncate under, a history view it
 * is looking through) to the same table of sentences.
 */
export type UndoRefusal = "not-your-turn" | "nothing-to-take-back";

export function undoRefusal(
  base: GameState,
  log: readonly Command[],
  side: Side
): UndoRefusal | null {
  return planUndo(base, log, side).why;
}

/** Where the floor is, what would be cut, and why not — from one replay. */
export interface UndoPlan {
  /** The last index no undo may cross (`undoFloor`). */
  floor: number;
  /** The index Undo would remove, or -1 (`lastAction`). */
  cut: number;
  /** The board the whole log produces — what is on screen. */
  state: GameState;
  /** Why it would do nothing, or null. */
  why: UndoRefusal | null;
}

/**
 * The whole answer, from **one** walk of the log.
 *
 * `undoFloor` and `undoRefusal` are the two named questions and both are
 * wrappers over this, because asking them separately is two full replays and
 * the second could disagree with the first about which board it measured. The
 * store cuts with the same plan it refuses with.
 */
export function planUndo(base: GameState, log: readonly Command[], side: Side): UndoPlan {
  const bottom = lastFloor(log);
  const { starts, state } = walkTurns(base, log, side, bottom);
  const began = starts.length > 0 ? starts[starts.length - 1].at : -1;
  const floor = Math.max(bottom, began);
  const cut = lastAction(log, floor);
  const why: UndoRefusal | null =
    state.position.turn !== side
      ? "not-your-turn"
      : cut < 0
        ? "nothing-to-take-back"
        : null;
  return { floor, cut, state, why };
}

/** A log with one action taken out of it, and the board it now reaches. */
export interface Cut {
  state: GameState;
  /** The commands that survived, in order. */
  log: Command[];
  /**
   * Indices of the **input** log that did not survive, ascending: `cut`
   * itself, plus every setting the reducer then refused.
   *
   * It is the cut that crosses the wire, never the resulting log (§29 U3):
   * a room broadcasts these numbers and every client makes the same cut, so
   * one implementation decides and nobody re-derives it.
   */
  dropped: number[];
}

/**
 * Replay `log` without index `cut` — the one implementation of Undo's cut,
 * called by `GameStore.undo` and by the server (§29 U2, U3).
 *
 * **A setting the cut orphans is dropped, never thrown.** Undo the `build`
 * that made a foundry and the `set-target` aimed at it configures a machine
 * that no longer exists; the reducer is right to refuse it, and the only sane
 * thing to do with it is leave it out. **Nothing else may fail** — everything
 * before the cut replays over an identical prefix and everything after it is a
 * setting by construction — so a non-setting refusing here is a bug and is
 * left to throw.
 *
 * `visit` sees each command that survived and the board it produced, so a
 * caller rebuilding a history pays for one replay rather than two.
 */
export function cutLog(
  base: GameState,
  log: readonly Command[],
  cut: number,
  visit?: (command: Command, state: GameState) => void
): Cut {
  let state = base;
  const kept: Command[] = [];
  const dropped: number[] = [];
  for (let i = 0; i < log.length; i++) {
    if (i === cut) {
      dropped.push(i);
      continue;
    }
    const command = log[i];
    let next: GameState;
    try {
      next = applyCommand(state, command);
    } catch (error) {
      if (error instanceof IllegalCommandError && isSetting(command)) {
        dropped.push(i);
        continue;
      }
      throw error;
    }
    state = next;
    kept.push(command);
    visit?.(command, state);
  }
  return { state, log: kept, dropped };
}

/**
 * Every square whose contents differ between two boards.
 *
 * **A generic diff, so it describes nothing in words and covers every action
 * that exists or will.** Whatever changes the board without a move lights what
 * changed — an undo, a history step — and a per-mechanic answer would be a
 * list to keep in step with the commands. So it is not only what *moves*: a
 * `build`, a `demolish`, a `pave`, an `upgrade`, a lance that only wounds, all
 * change a square with nothing crossing it, and each has to light the square it
 * changed.
 *
 * Every map a square's contents are read out of is walked, on both boards, or
 * a square that exists on one side alone is never asked about.
 *
 * Sorted, because a set's iteration order is a thing two clients could derive
 * differently from one log.
 */
export function changedSquares(a: GameState, b: GameState): number[] {
  const seen = new Set<number>();
  for (const state of [a, b]) {
    const P = state.position;
    // **Not `cells`.** Ground is never a change (designer, 2026-09-10): a
    // reveal or a stage boundary brings up a whole region at once, and
    // sweeping `cells` lit every square of it. Anything that *stands* on new
    // ground is in one of the maps below and lights on its own account.
    for (const map of [P.board, P.hurt, P.vitality, P.plating, P.variant]) {
      for (const sq of map.keys()) seen.add(sq);
    }
    const I = state.industry;
    for (const map of [
      I.heap,
      I.beltDir,
      I.beltOwner,
      I.beltOn,
      I.blueprintDir,
      I.blueprintOwner,
      I.beltCargo,
      I.structures
    ]) {
      for (const sq of map.keys()) seen.add(sq);
    }
  }

  const changed: number[] = [];
  for (const sq of seen) if (contentsAt(a, sq) !== contentsAt(b, sq)) changed.push(sq);
  return changed.sort((x, y) => x - y);
}

/**
 * What one square holds, as a string to compare.
 *
 * **Read through the accessors the reducer reads through, never the raw maps.**
 * Every one of them deletes its cell when the value returns to the default, so
 * a `Map.get` would call an absent heap and a heap of nought different — and a
 * default restated here is a second copy that drifts, which it had: `beltOn`
 * was `-1` here where `beltOnAt` answers `0`.
 *
 * **Ground is not in it.** A square does not change by coming into existence:
 * a board that grows — a reveal, a stage boundary, the opening of a solo run —
 * would otherwise tint every tile it brought up, which is a sweep and not
 * "what just changed". A bare tile and the void read alike here, so an undone
 * `pave` of an empty square lights nothing and one with a piece or a building
 * on it lights for the piece or the building.
 *
 * A building by kind, owner and health, and **not** its stock, masks or oven:
 * those churn on every tick, so including them would tint every machine on the
 * board after undoing a move somewhere else, which is noise and not "what
 * changed".
 */
function contentsAt(state: GameState, sq: number): string {
  const P = state.position;
  const I = state.industry;
  const cargo = I.beltCargo.get(sq);
  const structure = structureAt(I, sq);
  return [
    pieceAt(P, sq),
    healthOf(P, sq),
    platingAt(P, sq),
    variantAt(P, sq),
    heapAt(I, sq),
    beltDirAt(I, sq),
    beltOwnerAt(I, sq),
    beltOnAt(I, sq),
    blueprintDirAt(I, sq),
    blueprintOwnerAt(I, sq),
    cargo === undefined ? "" : cargo.join("."),
    structure ? `${structure.kind}.${structure.owner}.${structureHealthOf(structure)}` : ""
  ].join("/");
}
